import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { BillingPageSummaryResponse } from "../contracts/BillingPageSummaryResponse";
import { MemberBalanceResponse } from "../contracts/MemberBalanceResponse";
import {
  mapResponseToMonthEndReview,
  mapResponseToMonthlyStatement,
  mapResponseToBillingPageSummary,
  mapResponseToMemberBalance,
} from "../mappers/BillingMapper";
import { BillingPageSummary } from "../models/BillingPageSummary";
import { MonthEndReview } from "../models/MonthEndReview";
import { MonthEndReviewResponse } from "../contracts/MonthEndReviewResponse";
import { MonthEndPeriodRequest } from "../contracts/MonthEndPeriodRequest";
import { MonthlyStatementResponse } from "../contracts/MonthlyStatementResponse";
import { MonthlyStatement } from "../models/MonthlyStatement";
import { MonthEndConfirmAllResponse } from "../contracts/MonthEndConfirmAllResponse";
import { MonthEndGenerateResponse } from "../contracts/MonthEndGenerateResponse";
import { MemberBalance } from "../models/MemberBalance";
import { MemberBillingIssuesResponse } from "../contracts/MemberBillingIssuesResponse";
import { MemberBillingIssues } from "../models/MemberBillingIssues";
import { PaymentResponse } from "../../payments/contracts/PaymentResponse";
import { Payment } from "../../payments/models/Payment";
import { mapResponseToPayment } from "../../payments/mappers/PaymentMapper";

const BASE = "/billing";

export const fetchBillingSummary = createAsyncThunk<
  BillingPageSummary,
  void,
  { rejectValue: ApiErrorResponse }
>("billing/fetchSummary", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<BillingPageSummaryResponse>(`${BASE}/summary`);
    return mapResponseToBillingPageSummary(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load billing summary"));
  }
});

export const fetchMemberBalance = createAsyncThunk<
  MemberBalance,
  string,
  { rejectValue: ApiErrorResponse }
>("billing/fetchMemberBalance", async (memberId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MemberBalanceResponse>(`${BASE}/members/${memberId}/balance`);
    return mapResponseToMemberBalance(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load member balance"));
  }
});

/** Compact billing standing for a set of members (session roster, member list). */
export const fetchMemberIssues = createAsyncThunk<
  MemberBillingIssues[],
  string[],
  { rejectValue: ApiErrorResponse }
>("billing/fetchMemberIssues", async (memberIds, { rejectWithValue }) => {
  try {
    if (memberIds.length === 0) return [];
    const response = await axiosInstance.post<MemberBillingIssuesResponse[]>(
      `${BASE}/member-issues`,
      { memberIds }
    );
    return response.data.map((i) => ({ ...i }));
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load billing standings"));
  }
});

/** Create the missing previous-month subscription draft for one contract. */
export const createMissingDraft = createAsyncThunk<
  Payment,
  string,
  { rejectValue: ApiErrorResponse }
>("billing/createMissingDraft", async (contractId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentResponse>(
      `${BASE}/contracts/${contractId}/draft-missing`
    );
    return mapResponseToPayment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to create the missing draft"));
  }
});

/** The month-end reveal: one calendar month's subscription Payment drafts. */
export const fetchMonthEndReview = createAsyncThunk<
  MonthEndReview,
  MonthEndPeriodRequest,
  { rejectValue: ApiErrorResponse }
>("billing/fetchMonthEndReview", async ({ year, month }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MonthEndReviewResponse>(`${BASE}/month-end`, {
      params: { year, month },
    });
    return mapResponseToMonthEndReview(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the month-end review"));
  }
});

/** One-click confirm of every draft in the month (flagged ones included). */
export const confirmAllMonthEnd = createAsyncThunk<
  MonthEndConfirmAllResponse,
  MonthEndPeriodRequest,
  { rejectValue: ApiErrorResponse }
>("billing/confirmAllMonthEnd", async (period, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<MonthEndConfirmAllResponse>(
      `${BASE}/month-end/confirm-all`, period);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to confirm the month's drafts"));
  }
});

/** Draft the month's subscription Payments now, without waiting for the monthly job. */
export const generateMonthEndDrafts = createAsyncThunk<
  MonthEndGenerateResponse,
  MonthEndPeriodRequest,
  { rejectValue: ApiErrorResponse }
>("billing/generateMonthEndDrafts", async (period, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<MonthEndGenerateResponse>(
      `${BASE}/month-end/generate`, period);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to generate drafts"));
  }
});

/** The month's retrospective statement (money, delivery, per-member totals). */
export const fetchMonthlyStatement = createAsyncThunk<
  MonthlyStatement,
  MonthEndPeriodRequest,
  { rejectValue: ApiErrorResponse }
>("billing/fetchMonthlyStatement", async ({ year, month }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MonthlyStatementResponse>(`${BASE}/month-end/statement`, {
      params: { year, month },
    });
    return mapResponseToMonthlyStatement(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the monthly statement"));
  }
});
